import { useCallback, useEffect } from 'react';
import { apolloClient } from './apollo';
import { useSelector, useDispatch } from 'react-redux';
import { appSliceActions, appStateSelectors } from './redux';
import { SHEPARD_URL, USE_MODAL_AUTH } from './constants';
import { useLoginModal } from 'src/providers';

const strWindowFeatures = 'toolbar=no, menubar=no, width=600, height=700, top=100, left=100';
let windowObjectReference: Window | null = null;

/**
 * Determines whether the support login functionality should be hidden based on the current environment.
 *
 * This function evaluates various conditions such as whether the application is running inside an iframe,
 * the type of device or browser being used (e.g., Android WebView, iOS WebView, macOS WebView, VisionOS WebView),
 * and the presence of a specific environment variable (`REACT_APP_USE_MODAL_AUTH`).
 *
 * @returns {boolean} `true` if the support login should be hidden, otherwise `false`.
 *
 * Conditions:
 * - If `REACT_APP_USE_MODAL_AUTH` environment variable is set to `'true'`, the function always returns `true`.
 * - If the application is running inside an iframe (`window.self !== window.top`), returns `true`.
 * - If the user agent indicates an Android WebView, iOS WebView, macOS WebView, or VisionOS WebView, returns `true`.
 */
const shouldHideSupportLogin = (): boolean => {
  if (USE_MODAL_AUTH) {
    return true;
  }

  const isInIFrame = window.self !== window.top;
  const userAgent = window.navigator.userAgent || '';
  const standalone = (window.navigator as any).standalone;

  const isAndroidWebView =
    /Android/.test(userAgent) &&
    (/Version\/\d+\.\d+|; wv\)/.test(userAgent) || !/Chrome\/\d+\.\d+\.\d+\.\d+/.test(userAgent));

  const isiOSWebView =
    /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent) || standalone === true;

  const isMacOSWebView = /Macintosh.*AppleWebKit(?!.*Safari)/i.test(userAgent);

  const isVisionOSWebView = /RealityDevice.*AppleWebKit(?!.*Safari)/i.test(userAgent);
  const isWindowsWebView = /Windows.*WebView2/i.test(userAgent);
  const isSocialMediaBrowser = /FBAN|FBAV|Instagram|Twitter/i.test(userAgent);

  return (
    isInIFrame ||
    isAndroidWebView ||
    isiOSWebView ||
    isMacOSWebView ||
    isVisionOSWebView ||
    isWindowsWebView ||
    isSocialMediaBrowser
  );
};

export const useAuth = () => {
  const clientId = useSelector(appStateSelectors.getPreAPIClientId);
  const isLoggedIn = useSelector(appStateSelectors.getIsLoggedIn);
  const dispatch = useDispatch();

  const { open, close } = useLoginModal();

  useEffect(() => {
    const listener = ({ data, origin }: MessageEvent<any>) => {
      if (origin === SHEPARD_URL && (data?.accessToken || data?.idToken)) {
        close();
        dispatch(appSliceActions.setAuth(data));
      }
    };
    window.addEventListener('message', listener, false);
    return () => {
      // remove any existing event listeners
      window.removeEventListener('message', listener);
    };
  }, [close, dispatch]);

  const login = useCallback(
    (clientId: string) => {
      dispatch(appSliceActions.setClientId(clientId));

      const hideSupportLogin = shouldHideSupportLogin();
      const searchParams = new URLSearchParams([
        ['target', window.location.href],
        ['client_id', clientId],
        ['hide_support_login', hideSupportLogin.toString()],
      ]);
      const url = `${SHEPARD_URL}?${searchParams.toString()}`;
      if (hideSupportLogin) {
        open(url);
      } else {
        if (windowObjectReference === null || windowObjectReference.closed) {
          /* if the pointer to the window object in memory does not exist
	  or if such pointer exists but the window was closed */
          windowObjectReference = window.open(url, window.location.href, strWindowFeatures);
        } else {
          /* else the window reference must exist and the window
	  is not closed; therefore, we can bring it back on top of any other
	  window with the focus() method. There would be no need to re-create
	  the window or to reload the referenced resource. */
          windowObjectReference.focus();
        }
      }
    },
    [dispatch, open]
  );

  const logout = useCallback(async () => {
    await apolloClient.clearStore();
    dispatch(appSliceActions.logOut());
  }, [dispatch]);

  return { login, logout, isLoggedIn, clientId };
};
